import { openNotification } from '@common/helpers';
import { AuthHttp } from '@common/services';
import { getRequestParams } from '@helpers';
import { from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * GET /api/business-unit/list.
 *
 * @param page page index (Optional)
 * @param limit page size (Optional)
 * @param searchBy keyword (Optional)
 * @param sortBy field of sort (Optional)
 * @param sortOrder ASC/DESC (Optional)
 */
export function getBusinessUnitList(params) {
	const requestParams = getRequestParams(params);
	const url = params.searchBy
		? `/business-units/search?${requestParams}`
		: `/business-units/summary?${requestParams}`;
	return from(AuthHttp.get(url)).pipe(
		map(({ data }) => {
			return { data: data.content, count: data.totalElements };
		}),
		catchError((error) => {
			openNotification(
				'error',
				'Occurred error when load business unit list from server!'
			);
			return of({
				data: [],
				count: 0,
			});
		})
	);
}
/**
 * DELETE /business-units
 * @param buIds id of business units (Required)
 */
export function deleteBusinessUnits(buIds) {
	const url = `/business-units?buIds=${buIds}`;
	return from(AuthHttp.delete(url, {})).pipe(
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when delete business unit from server!'
			);
			return of({});
		})
	);
}

/**
 * POST /business-unit
 * Create business unit
 * @param
 */
export function createBU(params) {
	return from(AuthHttp.post('/business-units', params)).pipe(
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when create business unit!'
			);
			return of({});
		})
	);
}

/**
 * GET /api/companies.
 *
 */
export function getCompanies() {
	const url = '/companies';
	return from(AuthHttp.get(url)).pipe(
		map((res) => {
			if (res.data && !res.data.length) {
				openNotification('error', 'Cost center list is empty!');
				return [];
			}
			return res.data.map(({ id, name }) => ({ id, name }));
		}),
		catchError((e) => {
			openNotification(
				'error',
				'Occurred error when load company list from server!'
			);
			return of([]);
		})
	);
}

/**
 * GET /api/cost-centers
 * Get cost center list
 */
export function getCostCenters(companyId) {
	const url = `/cost-centers?companyId=${companyId}`;
	return from(AuthHttp.get(url)).pipe(
		map((res) => {
			if (res.data && !res.data.length) {
				openNotification('error', 'Cost center list is empty!');
				return [];
			}
			return res.data.map(({ id, name }) => ({ id, name }));
		}),
		catchError((e) => {
			openNotification(
				'error',
				'Occurred error when load cost center list from server!'
			);
			return of([]);
		})
	);
}

/**
 * GET /api/members
 * Get member list
 */
export function getCloudAccounts(companyId) {
	const url = `/cloud-accounts/company-members?companyId=${companyId}`;
	return from(AuthHttp.get(url)).pipe(
		map((res) => {
			return res.data;
		}),
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when get cloud accounts of business unit!'
			);
			return of([]);
		})
	);
}

/**
 * GET /api/members
 * Get member list
 */
export function getMembers(companyId) {
	const url = `users/company-members`;
	return from(AuthHttp.get(url)).pipe(
		map((res) => {
			return res.data;
		}),
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when get members of business unit!'
			);
			return of([]);
		})
	);
}

/**
 * GET /api/members
 * Get member list
 */
export function getLoginCostCenterMember(companyId) {
	const url = `users/login-costcenter-members`;
	return from(AuthHttp.get(url)).pipe(
		map((res) => {
			return res.data;
		}),
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when get members of business unit!'
			);
			return of([]);
		})
	);
}

/**
 * POST /business-unit/add-members
 * Add members of business unit
 * @param
 */
export function addMembers(buId, userIds, roleId) {
	return from(
		AuthHttp.post(
			`/business-units/addMember?buId=${buId}&userIds=${userIds}&roleId=${roleId}`,
			{}
		)
	).pipe(
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when add members of business unit!'
			);
			return of([]);
		})
	);
}

/**
 * POST /business-unit/add-cloud-account
 * Add cloud account
 * @param
 */
export function addCloudAccounts(buId, cloudAccountIds) {
	return from(
		AuthHttp.post(
			`/business-units/addCloudAccount?buId=${buId}&cloudAccountIds=${cloudAccountIds}`,
			{}
		)
	).pipe(
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when add cloud accounts of business unit!'
			);
			return of([]);
		})
	);
}

/**
 * GET /api/business-units/:id/cloud-accounts
 * Get all cloud accounts of BU
 * @param buId id of business units (Required)
 */
export function getCloudAccountsBU(id) {
	const url = `/business-units/${id}/cloud-accounts`;
	return from(AuthHttp.get(url)).pipe(
		map(({ data }) => {
			return { data: data.content, count: data.totalElements };
		}),
		catchError((e) => {
			const messages = e.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when load data from server!'
			);
			return of({ data: [], count: 0 });
		})
	);
}

/**
 * DELETE /business-units/cloud-accounts
 * @param ids id of cloud account of BU (Required)
 */
export function removeCloudAccountsBU(buId, ids) {
	const url = `/business-units/removeCloudAccount?buId=${buId}&cloudAccountIds=${ids}`;
	return from(AuthHttp.delete(url, {})).pipe(
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when remove cloud accounts from server!'
			);
			return of({});
		})
	);
}

/**
 * GET /api/business-units/:id/users
 * Get all user of BU
 * @param buId id of business units (Required)
 */
export function getUsersBU(id, params) {
	const requestParams = getRequestParams(params);
	const url = `/users/${id}/bu-members?${requestParams}`;
	return from(AuthHttp.get(url)).pipe(
		map(({ data }) => {
			return { data: data.content, count: data.totalElements };
		}),
		catchError((e) => {
			openNotification('error', 'Occurred error when load data from server!');
			return of({ data: [], count: 0 });
		})
	);
}

/**
 * DELETE /business-units/users
 * @param ids id of user of BU (Required)
 */
export function removeUsersBU(buId, ids) {
	const url = `/business-units/removeMember?buId=${buId}&userIds=${ids}`;
	return from(AuthHttp.delete(url, {})).pipe(
		catchError((e) => {
			const messages = e.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when remove users from server!'
			);
			return of({});
		})
	);
}

/**
 * GET /api/business-unit/:id.
 *
 * @param id ID of Business Unit (Required)
 */
export function getBusinessUnitDetails(id) {
	const url = `/business-units/${id}`;
	return from(AuthHttp.get(url)).pipe(
		map(({ data }) => {
			return data;
		}),
		catchError((e) => {
			const messages = e.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when get business unit details from server!'
			);
			return of({});
		})
	);
}

/**
 * POST /business-unit/:id
 * Edit Business Unit
 * @param id ID of Business Unit (Required)
 * @body {name: '', description: ''} (Required)
 */
export function updateBUDetails(id, params) {
	return from(AuthHttp.put(`/business-units/${id}`, params)).pipe(
		catchError((error) => {
			const messages = error.response && error.response.data.messages;
			openNotification(
				'error',
				messages
					? messages[0].message
					: 'Occurred error when update business unit!'
			);
			return of({});
		})
	);
}

/**
 * Update /api/public/business-units/setDefaultBu?buId={buId}&userId={userId}
 *
 * @param userId User Id (Required)
 * @param buId Business Unit Id (Required)
 */
export function setDefaultBU(params) {
	const url = `/business-units/setDefaultBu`;
	return from(AuthHttp.patch(url, params)).pipe(
		catchError((e) => {
			return of({});
		})
	);
}
